import React, { useCallback, useEffect, useState } from 'react';

import { Switch } from '@rbilabs/components-library';
import { AllowedEvent } from '@rbilabs/mparticle-client';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';

import { ArrowLink } from 'components/arrow-link';
import { GeolocationPermissionModalContainer } from 'components/geolocation-permission-modal';
import AccountLayout from 'components/layout/account';
import List, { ListItem, ListItemContent } from 'components/list';
import { BackArrowHistory } from 'components/navigation/back-arrow';
import AuthRequired from 'pages/authentication/auth-required';
import { useGeolocation } from 'state/geolocation';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { ClickEventComponentNames } from 'state/mParticle/constants';
import { useNearbyStoresContext } from 'state/nearby-stores';
import { isWeb } from 'utils/environment';

import { LinkContainer, ListContainer, LocationContainer, SubListGeo } from './styled';

const AccountSettings = () => {
  const { logRBIEvent, logNavigationClick } = useMParticleContext();
  const { restaurantGroup, resetRestaurantGroup } = useNearbyStoresContext();
  const { formatMessage } = useIntl();

  const [geolocationPermissionModalOpen, setGeolocationPermissionModalOpen] = useState(false);

  const [logEvent, setLogEvent] = useState(CustomEventNames.PAGE_VIEW);

  const enableGeolocation = useFlag(LaunchDarklyFlag.ENABLE_SETTINGS_GEOLOCATION);

  const {
    isPermissionGranted,
    isPermissionPrompt,
    isPermissionDenied,
    setPermissionChecking,
    loadCurrentPosition,
  } = useGeolocation();

  const sendLogEvent = useCallback(
    (component: string, status: boolean, event: CustomEventNames) => {
      logRBIEvent({
        name: event,
        type: EventTypes.Navigation,
        attributes: {
          component,
          text: `${status}`,
        },
      } as AllowedEvent);
    },
    [logRBIEvent]
  );

  const handleGeolocationPrompt = useCallback(() => {
    setLogEvent(CustomEventNames.CLICK_EVENT);

    if (isPermissionGranted) {
      setGeolocationPermissionModalOpen(true);
      resetRestaurantGroup();
      return;
    }

    if (isPermissionDenied) {
      setGeolocationPermissionModalOpen(true);
    } else if (isPermissionPrompt) {
      setPermissionChecking();
      loadCurrentPosition();
    }
  }, [
    isPermissionDenied,
    isPermissionGranted,
    isPermissionPrompt,
    loadCurrentPosition,
    resetRestaurantGroup,
    setPermissionChecking,
  ]);

  const dismissModal = useCallback(() => {
    setGeolocationPermissionModalOpen(false);

    if (isWeb) {
      setPermissionChecking();
    }
  }, [setPermissionChecking]);

  const handleNavigationClick = useCallback(() => {
    logNavigationClick(CustomEventNames.CLICK_EVENT);
  }, [logNavigationClick]);

  useEffect(() => {
    if (isPermissionGranted) {
      resetRestaurantGroup();
    }
    sendLogEvent(
      ClickEventComponentNames.ACCOUNT_SETTINGS_GEOLOCATION,
      isPermissionGranted,
      logEvent
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPermissionGranted]);

  useEffect(() => {
    if (geolocationPermissionModalOpen) {
      setGeolocationPermissionModalOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPermissionDenied]);

  return (
    <>
      <Helmet title={formatMessage({ id: 'accountSettings' })} />
      <AuthRequired>
        <BackArrowHistory />
        <AccountLayout
          header={formatMessage({ id: 'accountSettings' })}
          data-testid="account-settings-account-layout"
        >
          <ListContainer>
            <List>
              {enableGeolocation && (
                <>
                  <ListItem>
                    <ListItemContent>
                      <Switch
                        id="geolocation"
                        label={formatMessage({ id: 'geolocationLabelSwitch' })}
                        onClick={handleGeolocationPrompt}
                        checked={isPermissionGranted}
                        data-testid="account-settings-switch-geolocation"
                      />
                    </ListItemContent>
                  </ListItem>

                  {!isPermissionGranted && (
                    <ListItem>
                      <ListItemContent>
                        <SubListGeo>
                          <ArrowLink
                            data-testid="account-settings-option-geolocation"
                            to={formatMessage({ id: 'routes.settingsLocation' })}
                            onClick={handleNavigationClick}
                          >
                            <LinkContainer>
                              {formatMessage({ id: 'offerAccountSettings' })}
                              <LocationContainer>
                                {restaurantGroup
                                  ? restaurantGroup.name
                                  : formatMessage({ id: 'selectLabel' })}
                              </LocationContainer>
                            </LinkContainer>
                          </ArrowLink>
                        </SubListGeo>
                      </ListItemContent>
                    </ListItem>
                  )}
                </>
              )}
            </List>
          </ListContainer>
        </AccountLayout>
        <GeolocationPermissionModalContainer
          isOpen={geolocationPermissionModalOpen}
          dismissModal={dismissModal}
        />
      </AuthRequired>
    </>
  );
};

export default AccountSettings;
